import {Injectable, inject} from '@angular/core'
import {LocalNotificationSchema, LocalNotifications} from '@capacitor/local-notifications'
import {PreferencesStorageIds} from '@common/global-constants'
import {UveLogKey} from '@models/constants/log-key'
import {DevicePermissionStatus} from '@models/pemissions.models'
import {PreferencesApiService} from './storage/preferences-api.service'
import {LoggingService} from './logging.service'

/**
 * Wrapper de `@capacitor/local-notifications` con gestión de permisos y trazas.
 *
 * @description
 * Centraliza la solicitud/comprobación de permisos de notificaciones locales,
 * la programación y cancelación de notificaciones, y la consulta de las
 * pendientes. Ante permisos denegados genera una traza `warning` mediante
 * `LoggingService` con la clave `NotificationPermissionDeny`.
 *
 * La mayoría de métodos son estáticos porque no requieren estado y se pueden
 * invocar sin inyectar una instancia; `requestPermission` es el único método
 * de instancia porque necesita `LoggingService`.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * ```typescript
 * private readonly localNotification = inject(LocalNotificationService)
 *
 * await this.localNotification.requestPermission()
 * await LocalNotificationService.scheduleNotification([
 *   {id: 1, title: 'Visita en 15 min', body: 'Outlet #1234', schedule: {at: inFifteenMinutes}},
 * ])
 * ```
 *
 * ### Best Practices
 *
 * - Pedir permiso una única vez durante el onboarding, no en cada entrada de
 *   pantalla.
 * - Usar IDs deterministas en las notificaciones para poder cancelarlas con
 *   `cancelNotification`.
 *
 * ### Bad Uses / Errores comunes
 *
 * - Programar notificaciones sin comprobar permisos: `scheduleNotification`
 *   lo hace, pero si se invoca `LocalNotifications.schedule` directamente se
 *   pierde el check y la traza.
 */
@Injectable({
  providedIn: 'root',
})
export class LocalNotificationService {
  private readonly loggingService = inject(LoggingService)
  private readonly preferencesApiService = inject(PreferencesApiService)

  /** Requests permission for local notifications. */
  requestPermission = async (): Promise<void> => {
    const granted = await LocalNotifications.requestPermissions()
    const status = granted.display
    const previousStatus = (await this.preferencesApiService.get<DevicePermissionStatus | null>(
      PreferencesStorageIds.NOTIFICATION_PERMISSION_LAST_STATE
    )) as DevicePermissionStatus | null

    if (status === DevicePermissionStatus.DENIED) {
      const isFirstTime = !previousStatus
      const transitionFromGranted = previousStatus === DevicePermissionStatus.GRANTED
      if (isFirstTime || transitionFromGranted) {
        this.loggingService.warning(UveLogKey.Warning.NotificationPermissionDeny, granted)
      }
    }

    if (status === DevicePermissionStatus.GRANTED || status === DevicePermissionStatus.DENIED) {
      await this.preferencesApiService.set(PreferencesStorageIds.NOTIFICATION_PERMISSION_LAST_STATE, status)
    }
  }

  /**
   * Checks if the application has permission to display local notifications.
   *
   * @returns {Promise<boolean>} A promise that resolves to a boolean indicating whether permission is granted.
   */
  static readonly hasPermission = async (): Promise<boolean> => {
    const {display} = await LocalNotifications.checkPermissions()
    return display === DevicePermissionStatus.GRANTED
  }

  /**
   * Schedules one or more local notifications if the application has permission.
   *
   * @param {LocalNotificationSchema[]} notifications - An array of notifications to schedule.
   * @returns {Promise<void>} A promise that resolves when the scheduling is complete.
   */
  static readonly scheduleNotification = async (notifications: LocalNotificationSchema[]): Promise<void> => {
    if (await LocalNotificationService.hasPermission()) {
      await LocalNotifications.schedule({
        notifications,
      })
    }
  }

  /**
   * Cancels a specific local notification.
   *
   * @param {number} notificationId - The ID of the notification to cancel.
   * @returns {Promise<void>} A promise that resolves when the cancellation is complete.
   */
  static readonly cancelNotification = async (notificationId: number): Promise<void> => {
    await LocalNotifications.cancel({
      notifications: [{id: notificationId}],
    })
  }

  /**
   * Retrieves all pending local notifications.
   *
   * @returns {Promise<LocalNotificationSchema[]>} A promise that resolves to an array of pending notifications.
   */
  static readonly getPendingNotifications = async (): Promise<LocalNotificationSchema[]> => {
    const {notifications} = await LocalNotifications.getPending()
    return notifications
  }
}
